import * as React from 'react';
import { Save, Undo2 } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { EmptyState } from '@/components/app/EmptyState';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { humanizeError } from '@/lib/apiClient';
import { useNotificationConfig, useUpdateNotificationConfig } from '../hooks';
import { DiffViewer } from '../components/DiffViewer';
import { NotificationCategory, type NotificationGlobalConfig } from '../types';

type ConfigSection = 'caps' | 'cooldowns' | 'quietHours' | 'features' | 'engagement';

const SECTION_LABELS: Record<ConfigSection, { title: string; description: string }> = {
  caps: { title: 'Caps', description: 'Daily / weekly send ceilings. PROGRESSION is exempt from the global 24h cap.' },
  cooldowns: { title: 'Cooldowns', description: 'Minimum minutes between sends — globally and per category.' },
  quietHours: { title: 'Quiet hours', description: 'Default quiet window. Overridden per-user by forced windows.' },
  features: { title: 'Features', description: 'Runtime flags: Decision Brain, journeys, experiments, control plane, dashboards.' },
  engagement: { title: 'Engagement', description: 'Phase 1 feedback loop fallbacks and EMA tuning.' },
};

export function ConfigEditorPage() {
  const { data, isLoading, isError, error, refetch } = useNotificationConfig();
  const updateMut = useUpdateNotificationConfig();
  const confirm = useConfirmDialog();

  const [section, setSection] = React.useState<ConfigSection>('caps');
  const [draft, setDraft] = React.useState<NotificationGlobalConfig | null>(null);

  React.useEffect(() => {
    if (data?.config) setDraft(data.config);
  }, [data?.config]);

  const original = data?.config ?? null;
  const dirty = draft && original && JSON.stringify(draft) !== JSON.stringify(original);

  if (isLoading) return <LoadingState label="Loading config" />;
  if (isError)
    return (
      <ErrorState
        title="Config unavailable"
        description={humanizeError(error)}
        actionLabel="Retry"
        onAction={() => refetch()}
      />
    );
  // `!draft` catches the case where the fetch succeeded but the server
  // returned `config: null` (seed hasn't run) — never leave the user in
  // an infinite spinner.
  if (!original || !draft) {
    return (
      <EmptyState
        title="No config seeded"
        description="seedNotificationConfig has not run yet. Deploy admin-back or run the seed script."
      />
    );
  }

  const featuresDisabled = original.features?.controlPlaneEnabled === false;

  const handleSave = () => {
    if (!dirty || !draft) return;
    confirm({
      title: 'Save notification config?',
      description: 'Review the diff below. A reason is required and will be written to audit.',
      body: <DiffViewer before={original} after={draft} />,
      confirmLabel: 'Save',
      reasonRequired: true,
      reasonPlaceholder: 'e.g. "Dropping REMINDER cap to 3/24h after user complaints about volume"',
      onConfirm: async ({ reason }) => {
        const patch = buildPatch(original, draft);
        await updateMut.mutateAsync({ reason, patch });
      },
    });
  };

  const revert = () => {
    if (original) setDraft(JSON.parse(JSON.stringify(original)) as NotificationGlobalConfig);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications · Config"
        description="Firestore-backed knobs for the Decision Brain. Changes take effect within one cron tick."
        actions={
          featuresDisabled ? (
            <Badge variant="warning">Control plane disabled — read-only</Badge>
          ) : null
        }
      />

      {featuresDisabled && (
        <Card>
          <CardHeader>
            <CardTitle>Mutations disabled</CardTitle>
            <CardDescription>
              <code>features.controlPlaneEnabled</code> is off server-side. You can browse settings
              but PUT /config returns 503 until a deploy re-enables it.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav aria-label="Config sections" className="flex flex-col gap-1">
          {(Object.keys(SECTION_LABELS) as ConfigSection[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setSection(key)}
              className={
                section === key
                  ? 'rounded-md bg-accent px-3 py-2 text-left text-sm font-medium text-accent-foreground'
                  : 'rounded-md px-3 py-2 text-left text-sm font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }
            >
              {SECTION_LABELS[key].title}
            </button>
          ))}
        </nav>

        <Card>
          <CardHeader>
            <CardTitle>{SECTION_LABELS[section].title}</CardTitle>
            <CardDescription>{SECTION_LABELS[section].description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {section === 'caps' && <CapsSection draft={draft} onChange={setDraft} disabled={featuresDisabled} />}
            {section === 'cooldowns' && (
              <CooldownsSection draft={draft} onChange={setDraft} disabled={featuresDisabled} />
            )}
            {section === 'quietHours' && (
              <QuietHoursSection draft={draft} onChange={setDraft} disabled={featuresDisabled} />
            )}
            {section === 'features' && (
              <FeaturesSection draft={draft} onChange={setDraft} disabled={featuresDisabled} />
            )}
            {section === 'engagement' && (
              <EngagementSection draft={draft} onChange={setDraft} disabled={featuresDisabled} />
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending changes</CardTitle>
          <CardDescription>
            This diff is persisted to <code>notification_admin_audit</code> on save.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DiffViewer before={original} after={draft} />
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-xl border bg-card/80 p-3 shadow-card backdrop-blur">
        <Button variant="outline" onClick={revert} disabled={!dirty}>
          <Undo2 className="h-4 w-4" /> Revert
        </Button>
        <Button onClick={handleSave} disabled={!dirty || featuresDisabled || updateMut.isPending}>
          <Save className="h-4 w-4" />
          {updateMut.isPending ? 'Saving…' : 'Save…'}
        </Button>
      </div>
    </div>
  );
}

// ─── Section forms ──────────────────────────────────────────────────

interface SectionProps {
  draft: NotificationGlobalConfig;
  onChange: (next: NotificationGlobalConfig) => void;
  disabled?: boolean;
}

function CapsSection({ draft, onChange, disabled }: SectionProps) {
  return (
    <>
      <NumberField
        label="Global max · 24h"
        value={draft.caps.globalMax24h}
        onValueChange={(v) => onChange({ ...draft, caps: { ...draft.caps, globalMax24h: v } })}
        disabled={disabled}
        hint="PROGRESSION notifications are exempt."
      />
      <NumberField
        label="Global max · 7d"
        value={draft.caps.globalMax7d}
        onValueChange={(v) => onChange({ ...draft, caps: { ...draft.caps, globalMax7d: v } })}
        disabled={disabled}
      />
      <Separator />
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.values(NotificationCategory).map((cat) => (
          <NumberField
            key={cat}
            label={`${cat} · 24h`}
            value={draft.caps.categoryMax24h?.[cat] ?? 0}
            onValueChange={(v) =>
              onChange({
                ...draft,
                caps: {
                  ...draft.caps,
                  categoryMax24h: { ...draft.caps.categoryMax24h, [cat]: v },
                },
              })
            }
            disabled={disabled}
          />
        ))}
      </div>
    </>
  );
}

function CooldownsSection({ draft, onChange, disabled }: SectionProps) {
  return (
    <>
      <NumberField
        label="Global min spacing (minutes)"
        value={draft.cooldowns.globalMinSpacingMinutes}
        onValueChange={(v) =>
          onChange({ ...draft, cooldowns: { ...draft.cooldowns, globalMinSpacingMinutes: v } })
        }
        disabled={disabled}
      />
      <Separator />
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.values(NotificationCategory).map((cat) => (
          <NumberField
            key={cat}
            label={`${cat} spacing (min)`}
            value={draft.cooldowns.categoryMinSpacingMinutes?.[cat] ?? 0}
            onValueChange={(v) =>
              onChange({
                ...draft,
                cooldowns: {
                  ...draft.cooldowns,
                  categoryMinSpacingMinutes: { ...draft.cooldowns.categoryMinSpacingMinutes, [cat]: v },
                },
              })
            }
            disabled={disabled}
          />
        ))}
      </div>
    </>
  );
}

function QuietHoursSection({ draft, onChange, disabled }: SectionProps) {
  return (
    <>
      <NumberField
        label="Default start hour (0–23)"
        value={draft.quietHours.defaultStart}
        onValueChange={(v) => onChange({ ...draft, quietHours: { ...draft.quietHours, defaultStart: v } })}
        disabled={disabled}
        min={0}
        max={23}
      />
      <NumberField
        label="Default end hour (0–23)"
        value={draft.quietHours.defaultEnd}
        onValueChange={(v) => onChange({ ...draft, quietHours: { ...draft.quietHours, defaultEnd: v } })}
        disabled={disabled}
        min={0}
        max={23}
      />
      <BoolField
        label="Allow urgent during quiet hours"
        hint="System/SYSTEM priority=urgent can still send during quiet hours if enabled."
        value={draft.quietHours.allowUrgentDuring}
        onValueChange={(v) =>
          onChange({ ...draft, quietHours: { ...draft.quietHours, allowUrgentDuring: v } })
        }
        disabled={disabled}
      />
    </>
  );
}

const FEATURE_FLAGS = [
  { key: 'decisionBrainEnabled', label: 'Decision Brain' },
  { key: 'experimentsEnabled', label: 'Experiments' },
  { key: 'journeysEnabled', label: 'Journeys' },
  { key: 'engagementScoringEnabled', label: 'Engagement scoring' },
  { key: 'orchestratorEnabled', label: 'Intent orchestrator (Phase 2)' },
  { key: 'personalizedTimingEnabled', label: 'Personalized timing (Phase 3)' },
  { key: 'healthDashboardEnabled', label: 'Health dashboard (Phase 4A)' },
  { key: 'alertEvaluatorEnabled', label: 'Alert evaluator (Phase 4A)' },
  { key: 'controlPlaneEnabled', label: 'Control plane (Phase 4B)' },
  { key: 'adminUiEnabled', label: 'Admin UI (Phase 4C)' },
  { key: 'advancedTimingEnabled', label: 'Advanced timing (Phase 4D)' },
] as const;

function FeaturesSection({ draft, onChange, disabled }: SectionProps) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {FEATURE_FLAGS.map(({ key, label }) => (
        <BoolField
          key={key}
          label={label}
          value={Boolean((draft.features as unknown as Record<string, boolean | undefined>)[key])}
          onValueChange={(v) => onChange({ ...draft, features: { ...draft.features, [key]: v } })}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function EngagementSection({ draft, onChange, disabled }: SectionProps) {
  const engagement = draft.engagement ?? {
    fallbackMax24h: 6,
    fallbackMax7d: 20,
    emaAlpha: 0.15,
    signalEnableThreshold: 3,
    signalDisableThreshold: 1,
    openWindowHours: {} as Record<NotificationCategory, number>,
  };
  return (
    <>
      <NumberField
        label="Fallback max · 24h"
        value={engagement.fallbackMax24h}
        onValueChange={(v) => onChange({ ...draft, engagement: { ...engagement, fallbackMax24h: v } })}
        disabled={disabled}
      />
      <NumberField
        label="Fallback max · 7d"
        value={engagement.fallbackMax7d}
        onValueChange={(v) => onChange({ ...draft, engagement: { ...engagement, fallbackMax7d: v } })}
        disabled={disabled}
      />
      <NumberField
        label="EMA α (×1000)"
        hint="Stored as 0..1 but edited as an integer for precision. e.g. 150 = α=0.15"
        value={Math.round(engagement.emaAlpha * 1000)}
        onValueChange={(v) => onChange({ ...draft, engagement: { ...engagement, emaAlpha: v / 1000 } })}
        disabled={disabled}
        min={0}
        max={1000}
      />
      <NumberField
        label="Signal enable threshold"
        value={engagement.signalEnableThreshold}
        onValueChange={(v) =>
          onChange({ ...draft, engagement: { ...engagement, signalEnableThreshold: v } })
        }
        disabled={disabled}
      />
      <NumberField
        label="Signal disable threshold"
        value={engagement.signalDisableThreshold}
        onValueChange={(v) =>
          onChange({ ...draft, engagement: { ...engagement, signalDisableThreshold: v } })
        }
        disabled={disabled}
      />
    </>
  );
}

// ─── Shared field atoms ──────────────────────────────────────────────

function NumberField({
  label,
  value,
  onValueChange,
  disabled,
  hint,
  min,
  max,
}: {
  label: string;
  value: number;
  onValueChange: (v: number) => void;
  disabled?: boolean;
  hint?: string;
  min?: number;
  max?: number;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          onValueChange(v);
        }}
        disabled={disabled}
        min={min}
        max={max}
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function BoolField({
  label,
  value,
  onValueChange,
  disabled,
  hint,
}: {
  label: string;
  value: boolean;
  onValueChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border p-3">
      <div className="min-w-0 space-y-0.5">
        <Label>{label}</Label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch checked={value} onCheckedChange={onValueChange} disabled={disabled} />
    </div>
  );
}

function buildPatch(before: NotificationGlobalConfig, after: NotificationGlobalConfig): Partial<NotificationGlobalConfig> {
  const patch: Partial<NotificationGlobalConfig> = {};
  const sections = ['caps', 'cooldowns', 'quietHours', 'features', 'engagement'] as const;
  for (const s of sections) {
    const beforeSection = (before as unknown as Record<string, unknown>)[s];
    const afterSection = (after as unknown as Record<string, unknown>)[s];
    if (JSON.stringify(beforeSection) !== JSON.stringify(afterSection)) {
      (patch as unknown as Record<string, unknown>)[s] = afterSection;
    }
  }
  return patch;
}
